import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { AlertCircle, Mail01, MarkerPin01, Phone } from "@untitledui/icons";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field";
import { PhoneNumber } from "@/components/common";
import { Illustration } from "@/components/shared-assets/illustrations";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { organizationQueryOptions } from "@/services/organization";
import { SearchSchema } from "../$organizationId";

export const Route = createFileRoute(
	"/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/contacts",
)({
	component: ContactsTabComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function formatAddress(
	address: {
		street1?: string | null;
		city?: string | null;
		state?: string | null;
		postalCode?: string | null;
	} | null,
): string | null {
	if (!address) return null;
	const parts = [
		address.street1,
		address.city,
		address.state,
		address.postalCode,
	].filter(Boolean);
	return parts.length > 0 ? parts.join(", ") : null;
}

function ContactsTabComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(admin-app)/_admin/admin/organizations/$organizationId/contacts",
	});
	// Get organization data from parent's queries (shared cache - no extra network requests!)
	const orgById = useQuery({
		...organizationQueryOptions.detail(params.organizationId),
		retry: 1, // Fail fast
	});

	// This tab NEEDS orgContacts data
	const orgContacts = useQuery({
		...organizationQueryOptions.contacts.list(params.organizationId),
		retry: 1, // Fail fast
	});

	const primaryContact = orgContacts.data?.find(
		(contact) =>
			contact.contactType === "Primary" || contact.contactTypeCode === 1,
	);
	const billingContact = orgContacts.data?.find(
		(contact) =>
			contact.contactType === "Billing" || contact.contactTypeCode === 2,
	);

	// Check if THIS TAB's data is loading or has errors
	const isLoading = orgById.isLoading || orgContacts.isLoading;
	const hasError = orgById.isError || orgContacts.isError;

	if (!isLoading && hasError)
		return (
			<div className="flex flex-col items-center justify-center h-full gap-4">
				<Illustration type="cloud" size="sm">
					<div className="bg-error-500 rounded-full p-3">
						<AlertCircle className="size-6" />
					</div>
				</Illustration>
				<div className="flex flex-col items-center justify-center pt-4">
					<p className="text-gray-900 font-semibold text-md">
						{t("admin.organizations.errors.errorLoadingPage")}
					</p>
					<div className="w-[60%]">
						<p className="text-gray-600 font-normal text-wrap text-center text-sm mt-2">
							{t("admin.organizations.errors.errorLoadingContent")}
						</p>
					</div>
				</div>
				{/* <div className="flex gap-3 justify-center">
					<Button
						color="primary"
						size="md"
						onClick={() => {
							// Reset the error boundary
							queryErrorResetBoundary.reset();
							// Invalidate the route to reload loaders
							router.invalidate();
						}}
					>
						{t("common.actions.retry")}
					</Button>
				</div> */}
			</div>
		);

	return (
		<div className="bg-white pt-2">
			{!isLoading && !hasError && (
				<div className="space-y-6">
					<div>
						<p className="text-sm font-bold text-gray-800 mb-4 px-3">
							{t("admin.organizations.drawer.contactsTab.primaryContact")}
						</p>
						<DetailFieldGrid className="bg-gray-50 rounded-lg p-4">
							<DetailFieldSection>
								<DetailField
									label={t("Organization.Name", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										[primaryContact?.firstName, primaryContact?.lastName]
											.filter(Boolean)
											.join(" ") || null
									}
									valueClassName={
										!primaryContact?.firstName && !primaryContact?.lastName
											? "font-bold text-gray-400"
											: "font-bold text-gray-900"
									}
								/>

								<DetailField
									label={t("OrganizationContact.Title", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={primaryContact?.title}
									valueClassName="font-bold text-gray-900"
								/>

								<DetailField
									label={t("OrganizationContact.Email", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										primaryContact?.email ? (
											<div className="flex items-center gap-2">
												<Mail01 size={15} className="text-gray-400" />
												{primaryContact?.email}
											</div>
										) : null
									}
									valueClassName="font-bold text-gray-900"
								/>
							</DetailFieldSection>
							<DetailFieldSection>
								<DetailField
									label={t("OrganizationContact.Phone", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										primaryContact?.phone ? (
											<div className="flex items-center gap-2">
												<Phone size={15} className="text-gray-400" />
												<PhoneNumber phoneNumber={primaryContact.phone} />
											</div>
										) : null
									}
									valueClassName="font-bold text-gray-900"
								/>

								<DetailField
									label={t("OrganizationContact.Address", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={(() => {
										const addr = formatAddress(primaryContact?.address ?? null);
										return addr ? (
											<div className="flex items-start gap-2">
												<MarkerPin01
													size={15}
													className="text-gray-400 mt-0.5 shrink-0"
												/>
												{addr}
											</div>
										) : null;
									})()}
									valueClassName="font-bold text-gray-900"
								/>
							</DetailFieldSection>
						</DetailFieldGrid>
					</div>
					<div className="w-[50%] h-px bg-border-secondary my-8" />
					<div>
						<p className="text-sm font-bold text-gray-800 mb-4 px-3">
							{t("admin.organizations.drawer.contactsTab.billingContact")}
						</p>
						<DetailFieldGrid className="bg-gray-50 rounded-lg p-4">
							<DetailFieldSection>
								<DetailField
									label={t("Organization.Name", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										[billingContact?.firstName, billingContact?.lastName]
											.filter(Boolean)
											.join(" ") || null
									}
									valueClassName={
										!billingContact?.firstName && !billingContact?.lastName
											? "font-bold text-gray-400"
											: "font-bold text-gray-900"
									}
								/>

								<DetailField
									label={t("OrganizationContact.Title", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={billingContact?.title}
									valueClassName="font-bold text-gray-900"
								/>

								<DetailField
									label={t("OrganizationContact.Email", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										billingContact?.email ? (
											<div className="flex items-center gap-2">
												<Mail01 size={15} className="text-gray-400" />
												{billingContact?.email}
											</div>
										) : null
									}
									valueClassName="font-bold text-gray-900"
								/>
							</DetailFieldSection>
							<DetailFieldSection>
								<DetailField
									label={t("OrganizationContact.Phone", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={
										billingContact?.phone ? (
											<div className="flex items-center gap-2">
												<Phone size={15} className="text-gray-400" />
												<PhoneNumber phoneNumber={billingContact.phone} />
											</div>
										) : null
									}
									valueClassName="font-bold text-gray-900"
								/>
								<DetailField
									label={t("OrganizationContact.Address", {
										useFieldInfo: true,
									}).toUpperCase()}
									value={(() => {
										const addr = formatAddress(billingContact?.address ?? null);
										return addr ? (
											<div className="flex items-start gap-2">
												<MarkerPin01
													size={15}
													className="text-gray-400 mt-0.5 shrink-0"
												/>
												{addr}
											</div>
										) : null;
									})()}
									valueClassName="font-bold text-gray-900"
								/>
							</DetailFieldSection>
						</DetailFieldGrid>
					</div>
				</div>
			)}
		</div>
	);
}
